import useFetch from "use-http"
import { getAbsoluteServerUrl } from "../config/config"
import { ADD_ALL_ID } from "../layouts/components/map-municipality-search/MapMunicipalitySearchForm"
import { errorDeRespuesta } from "./map-error.util"

export interface MapMunicipality {
  id: number,
  name: string,
  province: MapProvince
}

/**
 * Builds a stable HTML-safe component key for the <GeoJSON> layer from metric
 * entries, independent of ordering (so it re-renders only on real data change,
 * not on reordering). Returns a short hash, or "empty" when there is no data.
 */
export function datasetSignature(
  entries: Array<{ section_id?: number, municipality_id?: string, province_id?: number, value: number }>
): string {
  const parts = entries
    .map((entry) => {
      const id = entry.section_id !== undefined
        ? entry.section_id
        : entry.municipality_id !== undefined
          ? entry.municipality_id
          : entry.province_id
      return id === undefined ? null : `${id}=${entry.value}`
    })
    .filter((part) => part !== null)

  if (parts.length === 0) return 'empty'

  parts!.sort()
  return `x${parts!.length}-${fnv1a(parts!.join('|'))}`
}

/**
 * Builds the query string for the municipality autocomplete, URL-encoding the
 * name so special chars (spaces, &, =, %, accents) don't break the request.
 */
export function buildMunicipalityAutocompleteQuery(value: string, provinceId: number | undefined): string {
  const params: string[] = [`name=${encodeURIComponent(value)}`]
  if (provinceId !== undefined) params.push(`province=${provinceId}`)
  return `?${params.join('&')}`
}

// FNV-1a 32-bit, lowercase hex. Compact, stable, good enough for a component key.
function fnv1a(input: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = (hash * 0x01000193) & 0xffffffff
  }
  return hash.toString(16)
}

export interface MapMunicipalityWitGeojson extends MapMunicipality {
  autonomousCommunity: MapAutonomousCommunity,
  geojson?: Highcharts.GeoJSON
}

export type MapSectionMetric = {
  section_id: number,
  year: string,
  value: number
}

export type MapMunicipalityMetric = {
  municipality_id: string,
  value: number
}

export type MapProvinceMetric = {
  province_id: number,
  year: string,
  value: number
}

export type MapAutonomousCommunity = {
  id: number,
  name: string
}

export type MapProvince = {
  id: number,
  name: string,
  autonomousCommunity: MapAutonomousCommunity
}

export function useGetAutonomousCommunities() {
  return useFetch<MapAutonomousCommunity[]>(getAbsoluteServerUrl('/map/autonomous_communities'), { responseType: 'json' }, [])
}

export type MapYear = {
  id: number,
  name: string
}

export async function getMapYears(signal?: AbortSignal) {
  const response = await fetch(getAbsoluteServerUrl(`/map/years`), { signal });
  if (response.ok) {
    return (await response.json());
  }
}

export async function getAutonomousCommunities(signal?: AbortSignal) {
  const response = await fetch(getAbsoluteServerUrl(`/map/autonomous_communities`), { signal });
  if (response.ok) {
    return (await response.json());
  }
  // throw error
}

export async function getMapMunicipalityGeojson(municipalityId: number, signal?: AbortSignal) {
  const response = await fetch(getAbsoluteServerUrl(`/map/municipalities/${municipalityId}/geojson`), { signal });
  if (response.ok) {
    return await response.json();
  }
}

export function useGetMunicipalityMetrics(municipalityId: number, metricId: number) {
  return useFetch<MapSectionMetric[]>(getAbsoluteServerUrl(`/map/municipalities/${municipalityId}/metrics/${metricId}`), { responseType: 'json' }, [])
}

export function useGetProvincesMetrics(metricId: number) {
  return useFetch<MapProvinceMetric[]>(getAbsoluteServerUrl(`/map/provinces/metrics/${metricId}`), { responseType: 'json' }, [])
}

export async function getProvincesMetrics(metricId: number, signal?: AbortSignal) {
  const response = await fetch(getAbsoluteServerUrl(`/map/provinces/metrics/${metricId}`), { signal });
  if (response.ok) {
    return await response.json();
  }
}

export function useGetMetricsFor(metricId: number, { autonomousCommunities, provinces, municipalities }:
  { autonomousCommunities: MapAutonomousCommunity[], provinces: MapProvince[], municipalities: MapMunicipality[] }) {
  const arrayToQueryParam = (name: string, list: { id: number }[]) =>
    list.filter(it => it.id !== ADD_ALL_ID).map((it, index) => `${name}[${index}]=${it.id}`)
      .join('&')
  const params = [
    { name: 'autonomousCommunities', values: autonomousCommunities },
    { name: 'provinces', values: provinces },
    { name: 'municipalities', values: municipalities }
  ].filter(it => it.values.length).map(it => arrayToQueryParam(it.name, it.values)).join('&')
  return useFetch<MapSectionMetric[]>(getAbsoluteServerUrl(`/map/metrics/${metricId}?${params}`), { responseType: 'json' }, [metricId, autonomousCommunities, provinces, municipalities])
}

export async function getMetricsFor(metricId: number, { autonomousCommunities, provinces, municipalities }:
  { autonomousCommunities: MapAutonomousCommunity[], provinces: MapProvince[], municipalities: MapMunicipality[] }, signal?: AbortSignal) {
  const arrayToQueryParam = (name: string, list: { id: number }[]) =>
    list.filter(it => it.id !== ADD_ALL_ID).map((it, index) => `${name}[${index}]=${it.id}`)
      .join('&')
  const params = [
    { name: 'autonomousCommunities', values: autonomousCommunities },
    { name: 'provinces', values: provinces },
    { name: 'municipalities', values: municipalities }
  ].filter(it => it.values.length).map(it => arrayToQueryParam(it.name, it.values)).join('&')

  const response = await fetch(getAbsoluteServerUrl(`/map/metrics/${metricId}?${params}`), { signal });
  if (response.ok) {
    return await response.json();
  }
  // el estado viaja en el error para que el mapa pueda avisar al usuario
  throw errorDeRespuesta(response.status);
}

export async function getAutonomousCommunityProvinces(autonomousCommunity: MapAutonomousCommunity, signal?: AbortSignal) {
  const response = await fetch(getAbsoluteServerUrl(`/map/autonomous_communities/${autonomousCommunity.id}/provinces`), { signal });
  if (response.ok) {
    return (await response.json()).map((data: any) => ({ ...data, autonomousCommunity: autonomousCommunity }))
  }
  // throw error
}

export async function getProvinceMunicipalities(province: MapProvince, signal?: AbortSignal) {
  const response = await fetch(getAbsoluteServerUrl(`/map/autonomous_communities/${province.autonomousCommunity.id}/provinces/${province.id}/municipalities`), { signal });
  if (response.ok) {
    return (await response.json()).map((data: any) => ({ ...data, province: province, autonomousCommunity: province.autonomousCommunity }))
  }
  // throw error
}

export function useGetMunicipalities() {
  return useFetch<MapMunicipality[]>(getAbsoluteServerUrl(`/map/municipalities`), { responseType: 'json' }, [])
}
